import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { PaginatedMetadata } from '@/types'
import { PAGE_SIZE_OPTIONS, showingRangeLabel } from '@/lib/listFetch'

interface DataTablePaginationProps {
  metadata: PaginatedMetadata | undefined
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  pageSizeOptions?: readonly number[]
}

// Build a numbered page list with ellipsis around the active page.
function buildPageList(current: number, last: number): Array<number | '...'> {
  if (last <= 7) return Array.from({ length: last }, (_, i) => i + 1)
  const pages: Array<number | '...'> = [1]
  const start = Math.max(2, current - 1)
  const end = Math.min(last - 1, current + 1)
  if (start > 2) pages.push('...')
  for (let p = start; p <= end; p++) pages.push(p)
  if (end < last - 1) pages.push('...')
  pages.push(last)
  return pages
}

export function DataTablePagination({
  metadata,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
}: DataTablePaginationProps) {
  const { t } = useTranslation('shell')
  const lastPage = metadata?.last_page ?? 1
  const currentPage = metadata?.current_page ?? page
  const atFirst = currentPage <= 1
  const atLast = currentPage >= lastPage

  const pages = buildPageList(currentPage, lastPage)

  return (
    <div className='flex flex-col items-center justify-between gap-3 px-2 py-3 sm:flex-row'>
      {/* Showing A-B of total */}
      <p className='text-sm text-muted-foreground'>
        {showingRangeLabel(metadata)}
      </p>

      <div className='flex items-center gap-4'>
        {/* Page-size selector wired to `limit` */}
        <div className='flex items-center gap-2'>
          <span className='text-sm text-muted-foreground'>
            {t('pagination.perPage')}
          </span>
          <Select
            value={String(pageSize)}
            onValueChange={v => onPageSizeChange(Number(v))}
          >
            <SelectTrigger className='h-8 w-[72px]'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map(opt => (
                <SelectItem key={opt} value={String(opt)}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Page controls */}
        <div className='flex items-center gap-1'>
          <Button
            variant='outline'
            size='icon'
            className='h-8 w-8'
            disabled={atFirst}
            onClick={() => onPageChange(1)}
            aria-label={t('pagination.firstPage')}
          >
            <ChevronsLeft className='h-4 w-4' />
          </Button>
          <Button
            variant='outline'
            size='icon'
            className='h-8 w-8'
            disabled={atFirst}
            onClick={() => onPageChange(currentPage - 1)}
            aria-label={t('pagination.previousPage')}
          >
            <ChevronLeft className='h-4 w-4' />
          </Button>

          {pages.map((p, i) =>
            p === '...' ? (
              <span
                key={`ellipsis-${i}`}
                className='px-2 text-sm text-muted-foreground'
              >
                ...
              </span>
            ) : (
              <Button
                key={p}
                variant={p === currentPage ? 'default' : 'outline'}
                size='icon'
                className={cn('h-8 w-8', p === currentPage && 'pointer-events-none')}
                onClick={() => onPageChange(p)}
                aria-current={p === currentPage ? 'page' : undefined}
              >
                {p}
              </Button>
            )
          )}

          <Button
            variant='outline'
            size='icon'
            className='h-8 w-8'
            disabled={atLast}
            onClick={() => onPageChange(currentPage + 1)}
            aria-label={t('pagination.nextPage')}
          >
            <ChevronRight className='h-4 w-4' />
          </Button>
          <Button
            variant='outline'
            size='icon'
            className='h-8 w-8'
            disabled={atLast}
            onClick={() => onPageChange(lastPage)}
            aria-label={t('pagination.lastPage')}
          >
            <ChevronsRight className='h-4 w-4' />
          </Button>
        </div>
      </div>
    </div>
  )
}

export default DataTablePagination
